import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

// ── Types ───────────────────────────────────────────────────────

export interface PricingTier {
  id: string
  name: string
  min_devices: number
  max_devices: number | null
  price_per_device_monthly: number
  yearly_discount_pct: number
  sort_order: number
}

export interface TenantSubscription {
  id: string
  tenant_id: string
  device_count: number
  billing_cycle: string
  price_per_device: number
  total_amount: number
  currency: string
  started_at: string
  expires_at: string
  auto_renew: boolean
  gateway_id: string | null
  status: string // active | grace | expired | cancelled
}

export interface PriceCalculation {
  device_count: number
  billing_cycle: string
  tier_name: string
  price_per_device_monthly: number
  yearly_discount_pct: number
  monthly_total: number
  period_months: number
  discount_amount: number
  amount_due: number
  currency: string
}

export interface PaymentGateway {
  id: string
  name: string
  provider: string
  currency: string
}

// ── Hooks ───────────────────────────────────────────────────────

export function usePricingTiers() {
  return useQuery({
    queryKey: ['pricing-tiers'],
    queryFn: () => api.get<{ tiers: PricingTier[] }>('/pricing').then((r) => r.data.tiers),
    staleTime: 5 * 60 * 1000,
  })
}

export function useSubscription() {
  return useQuery({
    queryKey: ['subscription'],
    queryFn: () =>
      api.get<{ subscription: TenantSubscription | null }>('/subscription').then((r) => r.data.subscription),
  })
}

export function useCalculatePrice() {
  return useMutation({
    mutationFn: (data: { device_count: number; billing_cycle: string }) =>
      api.post<PriceCalculation>('/subscription/calculate', data).then((r) => r.data),
  })
}

interface SubscribeResponse {
  subscription_payment_id: string
  amount: number
  currency: string
  gateway_id: string
  gateway_provider: string
  // Gateway-specific fields (populated after initiating payment)
  redirect_url?: string
  checkout_id?: string
  script_url?: string
  brands?: string
}

export function useSubscribe() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { device_count: number; billing_cycle: string; gateway_id: string }): Promise<SubscribeResponse> => {
      // Step 1: Create subscription payment record
      const sub = await api.post<SubscribeResponse>('/subscription/subscribe', data).then((r) => r.data)

      // Step 2: Initiate payment with the selected gateway
      const { gateway_provider, amount, currency } = sub
      let gatewayResult: Record<string, unknown> = {}

      switch (gateway_provider) {
        case 'qi_card':
          gatewayResult = await api.post('/payments/qi/initiate', { amount_iqd: amount, gateway_id: sub.gateway_id }).then((r) => r.data)
          break
        case 'zaincash':
          gatewayResult = await api.post('/payments/zaincash/initiate', { amount, currency, language: 'en', gateway_id: sub.gateway_id }).then((r) => r.data)
          break
        case 'hyperpay':
          gatewayResult = await api.post('/payments/hyperpay/checkout', { amount, currency, gateway_id: sub.gateway_id }).then((r) => r.data)
          break
      }

      return { ...sub, ...gatewayResult } as SubscribeResponse
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscription'] }),
  })
}

export function useUpgrade() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { device_count: number; billing_cycle: string; gateway_id: string }) =>
      api.post('/subscription/upgrade', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscription'] }),
  })
}

export function useActivateSubscription() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { subscription_payment_id: string }) =>
      api.post('/subscription/activate', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscription'] }),
  })
}

export function useAvailableGateways() {
  return useQuery({
    queryKey: ['available-gateways'],
    queryFn: () =>
      api.get<{ gateways: PaymentGateway[] }>('/payment-gateways/available').then((r) => r.data.gateways),
  })
}

// ── Wallet ──────────────────────────────────────────────────────

export interface TenantWallet {
  id: string
  tenant_id: string
  balance: number
  currency: string
  created_at: string
  updated_at: string
}

export interface WalletTransaction {
  id: string
  wallet_id: string
  type: string // topup | deduction | subscription
  amount: number
  description: string
  gateway_id: string | null
  created_at: string
}

export function useWallet() {
  return useQuery({
    queryKey: ['wallet'],
    queryFn: () => api.get<TenantWallet>('/wallet').then((r) => r.data),
  })
}

export function useWalletTransactions() {
  return useQuery({
    queryKey: ['wallet-transactions'],
    queryFn: () => api.get<WalletTransaction[]>('/wallet/transactions').then((r) => r.data),
  })
}

// ── Helpers ─────────────────────────────────────────────────────

export function getSubscriptionStatus(sub: TenantSubscription | null): 'none' | 'active' | 'grace' | 'expired' {
  if (!sub) return 'none'
  return sub.status as 'active' | 'grace' | 'expired'
}

export function getDaysRemaining(sub: TenantSubscription | null): number {
  if (!sub?.expires_at) return 0
  const diff = new Date(sub.expires_at).getTime() - Date.now()
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
}

export function isInGracePeriod(sub: TenantSubscription | null): boolean {
  return sub?.status === 'grace'
}

export function isExpired(sub: TenantSubscription | null): boolean {
  return sub?.status === 'expired'
}
